/**
 * Pending AutoTrade Execution Watch (R133, reworked R135) — the TradeDesk
 * Entry Qualifier's dashboard panel.
 *
 * Header carries the two TradeDesk switches (moved here from Settings):
 *   • FastLane        — per-second protection for hot trades (TrailSettings)
 *   • Entry Qualifier — hold auto entries for a pullback confirmation on a
 *     user-selectable timeframe (default 1m)
 * plus a ⚙ popover with their detail knobs (FastLane interval; Qualifier
 * pullback timeframe + condition + validity window).
 *
 * Each WAITING row can be Force-Approved (execute immediately — the full
 * TradeDesk gating ladder still applies) or Rejected (cancel — the trade is
 * never initiated). Rows are side-colour-coded like Trades Management.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CH_TYPE } from '../../styles/appUi';
import {
  Box, Paper, Typography, Chip, IconButton, Tooltip, Button, Switch,
  FormControlLabel, Popover, TextField, MenuItem, Divider,
} from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { settingsService } from '../../services/settings.service';
import trailService from '../../services/trail.service';
import OpenChartButton from './OpenChartButton';
import EmptyState from '../common/EmptyState';
import { formatPrice } from '../../utils/formatters';
import { chartOpenQuery } from '../../utils/timeframe';
import { intervalLabel as catalogIntervalLabel } from '../../utils/intervalCatalog';

interface PaeRow {
  id: number;
  symbol?: string;
  exchange?: string;
  decimalPrecision?: number;
  strategyName: string;
  action: 'BUY' | 'SHORT';
  strength?: string | null;
  signalPrice?: number | null;
  isl?: number | null;
  target?: number | null;
  ctfMinutes?: number | null;
  status: 'WAITING' | 'EXECUTED' | 'EXPIRED' | 'INVALIDATED' | 'CANCELLED';
  statusReason?: string | null;
  conditions: string;
  expiresAt: string;
  lastScore?: number | null;
  lastEmaValue?: number | null;
  lastEmaPeriod?: number | null;
  lastPrice?: number | null;
  lastCheckedAt?: string | null;
  createdAt: string;
}

const BULL = '#26a69a';
const BEAR = '#ef5350';
const BULL_MUTED = 'rgba(38,166,154,0.12)';
const BEAR_MUTED = 'rgba(239,83,80,0.12)';

const STATUS_COLOR: Record<PaeRow['status'], string> = {
  WAITING: '#ffb300',
  EXECUTED: BULL,
  EXPIRED: '#9e9e9e',
  INVALIDATED: BEAR,
  CANCELLED: '#9e9e9e',
};

function expiresIn(expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'now';
  const m = Math.floor(ms / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function intervalLabel(min?: number | null): string {
  return min == null ? '—' : catalogIntervalLabel(min);
}

function fmtIst(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

const GRID = 'minmax(120px,1.2fr) 44px 64px 96px 60px 120px 88px 88px 88px 88px minmax(210px,2fr) 92px 90px 104px';

const PendingAutoExecWatch: React.FC<{
  onOpenChart?: (symbol: string, exchange: string, extra?: Record<string, string>) => void;
  /** Reports the live WAITING-row count up to the parent (collapsible header badge). */
  onWaitingCountChange?: (n: number) => void;
}> = ({ onOpenChart, onWaitingCountChange }) => {
  const [rows, setRows] = useState<PaeRow[]>([]);
  const [, forceTick] = useState(0);

  // ── TradeDesk switches (moved from Settings) ─────────────────────────
  const [fastLaneOn, setFastLaneOn] = useState<boolean | null>(null);
  const [fastLaneSec, setFastLaneSec] = useState<number>(1);
  const [qualifierOn, setQualifierOn] = useState<boolean | null>(null);
  const [qualifierCond, setQualifierCond] = useState<string>('EITHER');
  const [qualifierTtl, setQualifierTtl] = useState<string>('');
  const [qualifierPullback, setQualifierPullback] = useState<number>(1);
  const [cfgAnchor, setCfgAnchor] = useState<HTMLElement | null>(null);
  const pbLabel = qualifierPullback >= 60 ? `${qualifierPullback / 60}H` : `${qualifierPullback}m`;

  useEffect(() => {
    trailService.getSettings()
      .then(s => {
        setFastLaneOn((s as any).fastLaneEnabled !== false);
        setFastLaneSec(Number((s as any).fastLaneIntervalSeconds) || 1);
      })
      .catch(() => setFastLaneOn(true));
    settingsService.getUserSettings()
      .then(r => {
        const d: any = r?.data?.data || {};
        setQualifierOn(!!d.entryQualifierEnabled);
        setQualifierCond(d.entryQualifierConditions || 'EITHER');
        setQualifierTtl(d.entryQualifierTtlMinutes != null ? String(d.entryQualifierTtlMinutes) : '');
        setQualifierPullback(Number(d.entryQualifierPullbackMinutes) || 1);
      })
      .catch(() => setQualifierOn(false));
  }, []);

  const toggleFastLane = async (on: boolean) => {
    setFastLaneOn(on);
    try {
      await trailService.saveSettings({ fastLaneEnabled: on } as any);
      toast.success(`FastLane ${on ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to save FastLane setting'); setFastLaneOn(!on); }
  };
  const toggleQualifier = async (on: boolean) => {
    setQualifierOn(on);
    try {
      await settingsService.updateUserSettings({ entryQualifierEnabled: on } as any);
      toast.success(`Entry Qualifier ${on ? `enabled — auto entries will wait for the ${pbLabel} pullback` : 'disabled — auto entries execute immediately'}`);
    } catch { toast.error('Failed to save Entry Qualifier setting'); setQualifierOn(!on); }
  };
  const saveDetails = async () => {
    try {
      const sec = Math.max(1, Math.min(10, Math.floor(Number(fastLaneSec) || 1)));
      await trailService.saveSettings({ fastLaneIntervalSeconds: sec } as any);
      await settingsService.updateUserSettings({
        entryQualifierConditions: qualifierCond,
        entryQualifierTtlMinutes: qualifierTtl === '' ? null : Math.max(5, Math.min(240, parseInt(qualifierTtl, 10) || 60)),
        entryQualifierPullbackMinutes: qualifierPullback,
      } as any);
      toast.success('TradeDesk settings saved');
      setCfgAnchor(null);
    } catch { toast.error('Failed to save'); }
  };

  // ── Rows ─────────────────────────────────────────────────────────────
  const fetchRows = useCallback(async () => {
    try {
      const r = await api.get<any>('/pending-auto-executions');
      setRows(r.data?.data || []);
    } catch { /* non-critical */ }
  }, []);

  useEffect(() => {
    fetchRows();
    const id = window.setInterval(fetchRows, 10_000);
    const tick = window.setInterval(() => forceTick(n => n + 1), 1_000);
    return () => { clearInterval(id); clearInterval(tick); };
  }, [fetchRows]);

  const approve = async (row: PaeRow) => {
    if (!window.confirm(`FORCE APPROVE ${row.action} ${row.symbol} now? The ${pbLabel} pullback wait is skipped; the TradeDesk gating ladder still applies.`)) return;
    try {
      const r = await api.post<any>(`/pending-auto-executions/${row.id}/execute-now`);
      toast.success(r.data?.message || 'Approved — executing');
      fetchRows();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || 'Failed');
      fetchRows();
    }
  };

  const reject = async (row: PaeRow) => {
    const verb = row.status === 'WAITING' ? 'REJECT' : 'Dismiss';
    if (!window.confirm(`${verb} ${row.symbol} ${row.action}? ${row.status === 'WAITING' ? 'The trade will NOT be initiated.' : ''}`)) return;
    try {
      await api.delete(`/pending-auto-executions/${row.id}`);
      toast.success(row.status === 'WAITING' ? 'Rejected — trade will not be initiated' : 'Dismissed');
      fetchRows();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed');
    }
  };

  const conditionText = (r: PaeRow): string => {
    const parts: string[] = [];
    const isBuy = r.action === 'BUY';
    if (r.conditions === 'EITHER' || r.conditions === 'TREND') {
      parts.push(`${pbLabel} score ${r.lastScore != null ? r.lastScore : '—'} → needs ${isBuy ? '≥ +20' : '≤ −20'}`);
    }
    if (r.conditions === 'EITHER' || r.conditions === 'EMA') {
      const dp = r.decimalPrecision ?? 2;
      parts.push(`EMA#4(${r.lastEmaPeriod ?? '—'}) ${r.lastEmaValue != null ? formatPrice(Number(r.lastEmaValue), dp) : '—'} → ${pbLabel} ${isBuy ? 'low ≤' : 'high ≥'}`);
    }
    return parts.join('  ·  ');
  };

  const allRows = rows;
  const waiting = allRows.filter(r => r.status === 'WAITING');

  // Report the waiting count up so the collapsible header can flag new entries.
  const realWaiting = waiting.length;
  useEffect(() => { onWaitingCountChange?.(realWaiting); }, [realWaiting, onWaitingCountChange]);

  const HCell: React.FC<{ children: React.ReactNode; right?: boolean }> = ({ children, right }) => (
    <Box sx={{ textAlign: right ? 'right' : 'left' }}>{children}</Box>
  );

  return (
    <Paper sx={{ p: 2.5, mb: 2, borderLeft: '4px solid #ffb300', boxShadow: 2, borderRadius: 2 }}>
      {/* ── Header: title + TradeDesk switches ─────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
          Pending AutoTrade Execution Watch ({waiting.length})
        </Typography>
        <Tooltip arrow title="TradeDesk Entry Qualifier — entry signals parked until the chosen pullback timeframe confirms a pullback entry. Force Approve executes immediately (gating ladder still applies); Reject cancels the entry so the trade is never initiated. Rows expire if the pullback never comes, are invalidated if price crosses the signal's ISL first, and are cancelled when the strategy's exit signal arrives.">
          <IconButton size="small" sx={{ color: 'text.secondary' }}>
            <InfoOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip arrow title="TradeDesk FastLane — per-second protection for HOT trades (spike mode active, or a breach-confirmation window running). Hot trades are price-checked every second instead of every 30s: the spike profit-lock ratchets from the live peak and SL/Target breaches are caught immediately. Cheap checks only; the full trail recompute stays on the 30s cycle. Falls back to the 30s monitor automatically on API trouble.">
          <FormControlLabel
            sx={{ mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: CH_TYPE.body, fontWeight: 700 } }}
            control={<Switch size="small" color="success" checked={fastLaneOn === true}
              disabled={fastLaneOn === null}
              onChange={e => toggleFastLane(e.target.checked)} />}
            label="FastLane"
          />
        </Tooltip>
        <Tooltip arrow title={`TradeDesk Entry Qualifier — when ON, auto-trade ENTRY signals do not execute at the signal price; they wait here for a pullback confirmation on your chosen timeframe (currently ${pbLabel}: Trend score crossing ±20, or a touch of EMA#4) and enter at the better pullback price. Set the timeframe + condition via the ⚙ button. When OFF, entries execute immediately. Exits are never delayed.`}>
          <FormControlLabel
            sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: CH_TYPE.body, fontWeight: 700 } }}
            control={<Switch size="small" color="warning" checked={qualifierOn === true}
              disabled={qualifierOn === null}
              onChange={e => toggleQualifier(e.target.checked)} />}
            label="Entry Qualifier"
          />
        </Tooltip>
        <Tooltip arrow title="TradeDesk detail settings — FastLane interval, Qualifier condition + validity window">
          <IconButton size="small" onClick={e => setCfgAnchor(e.currentTarget)} sx={{ color: 'text.secondary' }}>
            <SettingsIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
        <Popover
          open={!!cfgAnchor} anchorEl={cfgAnchor} onClose={() => setCfgAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ p: 2, width: 290, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ fontWeight: 800, fontSize: CH_TYPE.body }}>TradeDesk settings</Typography>
            <TextField
              size="small" type="number" label="FastLane interval (seconds)"
              value={fastLaneSec}
              onChange={e => setFastLaneSec(Number(e.target.value))}
              inputProps={{ min: 1, max: 10 }}
              helperText="1-10s. Zerodha floored to 2s server-side."
            />
            <Divider />
            <TextField
              select size="small" label="Pullback timeframe"
              value={qualifierPullback}
              onChange={e => setQualifierPullback(Number(e.target.value))}
              helperText="The timeframe the pullback is confirmed on."
            >
              <MenuItem value={1}>1 minute</MenuItem>
              <MenuItem value={3}>3 minutes</MenuItem>
              <MenuItem value={5}>5 minutes</MenuItem>
              <MenuItem value={15}>15 minutes</MenuItem>
              <MenuItem value={30}>30 minutes</MenuItem>
              <MenuItem value={60}>1 hour</MenuItem>
            </TextField>
            <TextField
              select size="small" label="Qualifier condition"
              value={qualifierCond}
              onChange={e => setQualifierCond(e.target.value)}
            >
              <MenuItem value="EITHER">Either (Trend cross OR EMA#4 touch)</MenuItem>
              <MenuItem value="TREND">{`Trend cross only (${pbLabel} score ±20)`}</MenuItem>
              <MenuItem value="EMA">EMA#4 touch only</MenuItem>
            </TextField>
            <TextField
              size="small" type="number" label="Qualifier validity (minutes)"
              value={qualifierTtl}
              onChange={e => setQualifierTtl(e.target.value)}
              inputProps={{ min: 5, max: 240 }}
              helperText="Blank = auto (one signal-timeframe bar)."
            />
            <Button size="small" variant="contained" onClick={saveDetails}>Save</Button>
          </Box>
        </Popover>
      </Box>

      {allRows.length === 0 ? (
        <EmptyState kind="inbox" size="sm" title="No parked entries"
          description={`With the Entry Qualifier ON, auto-trade entry signals wait here for a pullback confirmation (currently ${pbLabel}) before executing.`} />
      ) : (
        <Box sx={{ overflowX: 'auto', maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}>
          <Box sx={{ minWidth: 1320 }}>
            {/* Header row — sticky so column labels stay visible while scrolling. */}
            <Box sx={{
              display: 'grid', gridTemplateColumns: GRID, gap: 1, px: 0.5, py: 0.5, mb: 0.5,
              fontSize: CH_TYPE.small, fontWeight: 700, color: 'text.secondary',
              textTransform: 'uppercase', letterSpacing: 0.3,
              position: 'sticky', top: 0, zIndex: 3,
              bgcolor: (t) => t.palette.mode === 'dark' ? '#1c2128' : '#eef1f4',
            }}>
              <HCell>Symbol</HCell>
              <HCell>Chart</HCell>
              <HCell>Side</HCell>
              <HCell>Strategy</HCell>
              <HCell>TF</HCell>
              <HCell>Signal received</HCell>
              <HCell right>Signal px</HCell>
              <HCell right>ISL</HCell>
              <HCell right>Target</HCell>
              <HCell right>{`LTP (${pbLabel})`}</HCell>
              <HCell>Qualifies when</HCell>
              <HCell>Status</HCell>
              <HCell>Expires in</HCell>
              <HCell right>Actions</HCell>
            </Box>

            {allRows.map((r, idx) => {
              const dp = r.decimalPrecision ?? 2;
              const isBuy = r.action === 'BUY';
              const sideCol = isBuy ? BULL : BEAR;
              const col = STATUS_COLOR[r.status];
              const fmt = (v?: number | null) => v != null ? formatPrice(Number(v), dp) : '—';
              return (
                <Box key={r.id} sx={{
                  display: 'grid', gridTemplateColumns: GRID, gap: 1, py: 0.6, px: 0.5,
                  alignItems: 'center', fontSize: CH_TYPE.body,
                  borderBottom: theme => `1px solid ${theme.palette.divider}`,
                  opacity: r.status === 'WAITING' ? 1 : 0.6,
                  bgcolor: idx % 2 ? 'transparent' : 'rgba(128,128,128,0.045)',
                  '&:hover': { bgcolor: 'action.hover' },
                }}>
                  {/* Symbol — side-tinted like Trades Management */}
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.6,
                    bgcolor: isBuy ? BULL_MUTED : BEAR_MUTED,
                    borderLeft: `3px solid ${sideCol}`,
                    borderRadius: 0.5, px: 0.6, py: 0.3,
                  }}>
                    <Tooltip title={r.symbol || ''} arrow><Typography sx={{ fontWeight: 800, fontSize: CH_TYPE.body, color: sideCol, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{r.symbol}</Typography></Tooltip>
                    {r.exchange && <Chip label={r.exchange} size="small" sx={{ fontSize: CH_TYPE.micro, height: 18, '& .MuiChip-label': { px: 0.4 } }} />}
                  </Box>
                  {/* R149 — dedicated Chart column right after the symbol. */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {r.symbol && onOpenChart && (
                      <OpenChartButton onOpen={() => onOpenChart(r.symbol!, r.exchange || '', chartOpenQuery(r.ctfMinutes, r.strategyName))} />
                    )}
                  </Box>
                  <Chip label={r.action} size="small" sx={{
                    bgcolor: isBuy ? BULL_MUTED : BEAR_MUTED, color: sideCol,
                    fontWeight: 800, fontSize: CH_TYPE.small, height: 19,
                    border: `1px solid ${sideCol}55`, '& .MuiChip-label': { px: 0.6 }, width: 'fit-content',
                  }} />
                  <Box sx={{ fontWeight: 600 }}>
                    {r.strategyName}
                    {r.strength && (
                      <Typography component="span" sx={{ ml: 0.4, fontSize: CH_TYPE.micro, color: 'text.secondary' }}>· {r.strength}</Typography>
                    )}
                  </Box>
                  <Box sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{intervalLabel(r.ctfMinutes)}</Box>
                  <Box sx={{ fontSize: CH_TYPE.small, color: 'text.secondary' }}>{fmtIst(r.createdAt)}</Box>
                  <Box sx={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmt(r.signalPrice)}</Box>
                  <Box sx={{ textAlign: 'right', fontFamily: 'monospace', color: BEAR }}>{fmt(r.isl)}</Box>
                  <Box sx={{ textAlign: 'right', fontFamily: 'monospace', color: BULL }}>{fmt(r.target)}</Box>
                  <Box sx={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{fmt(r.lastPrice)}</Box>
                  <Box sx={{ fontSize: CH_TYPE.small, color: 'text.secondary', lineHeight: 1.3 }}>
                    {r.status === 'WAITING' ? conditionText(r) : (r.statusReason || '—')}
                  </Box>
                  <Box>
                    <Chip label={r.status} size="small" sx={{
                      bgcolor: `${col}22`, color: col, fontWeight: 700, fontSize: CH_TYPE.micro, height: 18,
                      border: `1px solid ${col}66`, '& .MuiChip-label': { px: 0.5 },
                    }} />
                  </Box>
                  <Box sx={{ fontFamily: 'monospace', fontSize: CH_TYPE.small, fontWeight: 700, color: r.status === 'WAITING' ? '#ffb300' : 'text.disabled' }}>
                    {r.status === 'WAITING' ? expiresIn(r.expiresAt) : '—'}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.3, justifyContent: 'flex-end' }}>
                    {r.status === 'WAITING' && (
                      <>
                        <Tooltip title="FORCE APPROVE — execute immediately, skipping the pullback wait (the TradeDesk gating ladder still applies)" arrow>
                          <IconButton size="small" onClick={() => approve(r)} sx={{ p: 0.25, color: BULL }}>
                            <CheckCircleIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="REJECT — cancel this entry; the trade will NOT be initiated by the auto-trade engine" arrow>
                          <IconButton size="small" onClick={() => reject(r)} sx={{ p: 0.25, color: BEAR }}>
                            <CancelIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {r.status !== 'WAITING' && (
                      <Tooltip title="Dismiss from list" arrow>
                        <IconButton size="small" onClick={() => reject(r)} sx={{ p: 0.25, color: 'text.secondary' }}>
                          <CancelIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default PendingAutoExecWatch;
